import type { Insight } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  SortableGrid,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useEffect,
  useState,
} from "react"
import {
  SortableTile,
  OverlayTile,
} from "./Tile"
import { useUpdateInsights } from "src/hooks"
import { ReorderInsightsErrorToast } from "src/toasts"

const idSeparator = "|-|"

interface SortableInsight {
  id: string;
  insight: Insight;
}

function insightToSortable(insight: Insight): SortableInsight {
  return {
    id: `${insight.key}${idSeparator}${insight.range}`,
    insight,
  }
}

function sortableToInsight(sortable: SortableInsight): Insight {
  return sortable.insight
}

interface Props {
  insights: Array<Insight>;
}

export function SortableTiles(props: Props) {
  const { insights } = props
  const updateInsights = useUpdateInsights()
  const { showToast } = useToasterContext()

  const [
    sortableInsights,
    setSortableInsights,
  ] = useState<Array<SortableInsight>>(
    insights.map(
      (insight) => insightToSortable(insight),
    ),
  )

  useEffect(
    () => {
      const newSortableInsights = insights.map(
        (insight) => insightToSortable(insight),
      )

      setSortableInsights(newSortableInsights)
    },
    [
      insights,
      setSortableInsights,
    ],
  )

  const updateInsightOrder = useCallback(
    (sortedInsights: Array<SortableInsight>) => {
      void (async () => {
        setSortableInsights(sortedInsights)

        const newInsights = sortedInsights.map(sortableToInsight)

        try {
          if (!updateInsights) {
            throw new Error("cannot reorder insights")
          }

          await updateInsights(newInsights)
        } catch {
          showToast(
            "reorder-insights-error-toast",
            ReorderInsightsErrorToast,
          )
        }
      })()
    },
    [
      setSortableInsights,
      updateInsights,
      showToast,
    ],
  )

  return (
    <SortableGrid
      items={sortableInsights}
      onChange={updateInsightOrder}
      SortableItem={SortableTile}
      OverlayItem={OverlayTile}
    />
  )
}
